import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Billing() {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [polling, setPolling] = useState(false);
  const [interval, setIntervalState] = useState("month"); // month | year

  useEffect(() => {
    api.get("/billing/plans")
      .then(({ data }) => setPlans(data.plans || []))
      .catch((err) => { console.error("Failed to load plans:", err); });
  }, []);

  // Poll session status if redirected from Stripe
  useEffect(() => {
    const sid = params.get("session_id");
    const status = params.get("status");
    if (status === "cancelled") { toast("Checkout cancelled"); setParams({}); return; }
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/billing/session/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Subscription activated!");
          refreshUser();
          setParams({});
          setPolling(false);
          return;
        }
      } catch (err) {
        console.error("Session poll failed:", err);
      }
      if (attempts > 10) { setPolling(false); setParams({}); return; }
      setTimeout(tick, 2000);
    };
    tick();
  }, [params, refreshUser, setParams]);

  const subscribe = async (tier) => {
    setLoading(tier);
    try {
      const { data } = await api.post("/billing/create-checkout-session", {
        tier,
        interval,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  const cancel = async () => {
    if (!confirm("Cancel subscription at end of current period?")) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription will cancel at period end");
      refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    }
  };

  const currentTier = user?.subscription_tier || "free";
  const isActive = user?.subscription_status === "active";

  const renderSubscribeLabel = ({ isCurrent, isLoading }) => {
    if (isCurrent) return "Current plan";
    if (isLoading) return "Redirecting…";
    return <>Subscribe <ArrowUpRight className="w-4 h-4" /></>;
  };

  return (
    <div className="p-5 sm:p-8 md:p-12 max-w-[1400px]">
      <div className="mb-8 sm:mb-12">
        <div className="overline mb-3">— Billing</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-tight" data-testid="billing-title">Plan.</h1>
        <p className="mt-3 text-sm sm:text-base text-white/60">Choose the plan that fits your volume. Cancel anytime.</p>
      </div>

      <div className="surface-card p-6 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="billing-current">
        <div>
          <div className="overline mb-1">Current plan</div>
          <div className="font-heading font-black text-3xl tracking-tighter capitalize">{currentTier}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-3 py-1 text-xs font-bold uppercase tracking-wider ${isActive ? "bg-klein/10 text-klein" : "bg-surface/5 text-white/60"}`}>
            {user?.subscription_status || "inactive"}
          </div>
          {isActive && (
            <button onClick={cancel} className="btn-ghost text-sm py-2" data-testid="billing-cancel-btn">Cancel plan</button>
          )}
        </div>
      </div>

      {/* Monthly / Annual toggle */}
      <div className="mb-8 inline-flex items-center gap-0 border border-white/15 p-1 bg-bone" data-testid="billing-interval-toggle">
        <button
          onClick={() => setIntervalState("month")}
          className={`px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${interval === "month" ? "bg-obsidian text-white" : "text-white/60 hover:text-white"}`}
          data-testid="billing-interval-month"
        >
          Monthly
        </button>
        <button
          onClick={() => setIntervalState("year")}
          className={`px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 ${interval === "year" ? "bg-obsidian text-white" : "text-white/60 hover:text-white"}`}
          data-testid="billing-interval-year"
        >
          Annual
          <span className={`text-[10px] px-1.5 py-0.5 ${interval === "year" ? "bg-klein text-white" : "bg-klein/10 text-klein"}`}>Save 2 months</span>
        </button>
      </div>

      {polling && <div className="surface-card p-4 mb-6 bg-klein/5 border-klein/20 text-klein text-sm" data-testid="billing-polling">Finalizing your subscription…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl">
        {plans.map((p) => {
          const isCurrent = currentTier === p.id && isActive;
          const price = interval === "year" ? p.price_annual : p.price_monthly;
          const perMonth = interval === "year" ? (p.price_annual / 12).toFixed(2) : null;
          return (
            <div key={p.id} className={`surface-card p-6 sm:p-8 ${p.popular ? "border-white" : ""}`} data-testid={`billing-plan-${p.id}`}>
              {p.popular && <div className="inline-block bg-klein text-white px-3 py-1 text-[10px] sm:text-xs font-bold tracking-wider uppercase mb-3 sm:mb-4">Most popular</div>}
              <div className="font-heading font-black text-2xl sm:text-3xl">{p.name}</div>
              <div className="my-4 sm:my-6 flex items-baseline gap-2 flex-wrap">
                <span className="font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter">${price}</span>
                <span className="text-white/60 text-sm sm:text-base">/{interval === "year" ? "year" : "month"}</span>
              </div>
              {perMonth && (
                <div className="-mt-2 sm:-mt-4 mb-4 text-sm text-white/50" data-testid={`billing-permonth-${p.id}`}>
                  ${perMonth}/month billed annually
                </div>
              )}
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-klein shrink-0 mt-0.5" /><span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                disabled={loading === p.id || isCurrent}
                onClick={() => subscribe(p.id)}
                className={`${p.popular ? "btn-primary" : "btn-secondary"} w-full justify-center`}
                data-testid={`billing-subscribe-${p.id}`}
              >
                {renderSubscribeLabel({ isCurrent, isLoading: loading === p.id })}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
